import { useTranslation } from "react-i18next";

import type { CcEscalation } from "@/api/code-companion";
import { useCcEscalations } from "@/hooks/useCodeCompanion";


/**
 * O que o run perguntou, e o que a pessoa respondeu (S-064 onda 5).
 *
 * Era a única intervenção humana no run autônomo e a única coisa que não sobrevivia a
 * ele: a pergunta ficava em `agent_executions.interrupt_payload`, um campo só,
 * sobrescrito na pausa seguinte; a resposta ia no payload de uma task, consumido; e o
 * autor não era registrado em lugar nenhum.
 *
 * Num produto que se descreve como "human always in control", esse era o registro mais
 * importante que faltava.
 *
 * **Aberta é ausência de resposta**, não uma coluna de status: status guardado pode
 * divergir do próprio dado.
 */

/** As opções, quando a pergunta tem resposta fechada. */
export function answerChoices(e: CcEscalation): string[] {
  return e.options ?? [];
}

/** O texto da pergunta, que pode estar em `text` ou no próprio payload. */
export function questionText(e: CcEscalation): string {
  const q = e.question ?? {};
  const text = q.text ?? q.question ?? q.detail;
  if (typeof text === "string" && text.trim()) return text;
  // Payload sem campo de texto: mostra o que veio, em vez de uma caixa muda.
  return Object.entries(q)
    .filter(([k]) => k !== "kind" && k !== "options")
    .map(([k, v]) => `${k}: ${typeof v === "string" ? v : JSON.stringify(v)}`)
    .join(" · ");
}

export default function EscalationPanel({
  projectId,
  runId,
}: {
  projectId: string;
  runId: string;
}) {
  const { t } = useTranslation();
  const { data } = useCcEscalations(projectId, runId);
  // 🔶 **O relógio da sessão saiu daqui** (#593). Este painel é histórico: as opções
  // dele são `<span>`, não botões. Quem decide clica nos `hitl-${op}` do `RunPanel`, e é
  // lá que o relógio, o aviso e o reconectar moram desde a #577. Manter uma segunda cópia
  // logo abaixo mostrava o mesmo alarme duas vezes na mesma coluna, oferecia reconectar a
  // quem só acompanha (este painel não é gateado por `mayControl`) e seguia avisando em
  // run cancelado com pergunta pendente, onde reconectar não resolve nada.
  // Run que nunca perguntou nada não precisa de bloco: o caso comum é não haver HITL.
  if (!data?.items.length) return null;

  return (
    <div
      data-testid="escalation-panel"
      className="overflow-hidden rounded-lg border border-border bg-card"
    >
      <div className="border-b border-border px-3 py-2 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
        {t("Asked of a person")}
      </div>
      <ul className="divide-y divide-border">
        {data.items.map((e) => (
          <li key={e.id} className="px-3 py-2 text-[11px]">
            <div className="flex flex-wrap items-baseline gap-2">
              <span
                data-testid={e.open ? "escalation-open" : "escalation-answered"}
                className="font-mono text-[10px] font-semibold uppercase"
                style={{ color: e.open ? "#b45309" : "#2b8f57" }}
              >
                {e.open ? t("waiting for you") : t("answered")}
              </span>
              {e.kind && (
                <span className="font-mono text-[10px] text-muted-foreground">{e.kind}</span>
              )}
              {e.object_name && (
                <span className="font-mono text-[10px]">{e.object_name}</span>
              )}
              <div className="flex-1" />
              {e.asked_at && (
                <span className="font-mono text-[10px] text-muted-foreground">
                  {new Date(e.asked_at).toLocaleString()}
                </span>
              )}
            </div>

            <div className="mt-0.5 break-words">{questionText(e)}</div>

            {/* As opções aparecem mesmo depois de respondida: é o que deixa a resposta
                legível ("escolheu redesign entre redesign e skip"). */}
            {answerChoices(e).length > 0 && (
              <div
                data-testid="escalation-options"
                className="mt-0.5 flex flex-wrap gap-1 font-mono text-[10px] text-muted-foreground"
              >
                {answerChoices(e).map((o) => (
                  <span key={o} className="rounded bg-muted px-1">
                    {o}
                  </span>
                ))}
              </div>
            )}

            {e.answer && (
              <div className="mt-0.5 break-words">
                <span className="text-muted-foreground">{t("answer")} </span>
                {Object.entries(e.answer)
                  .map(([k, v]) => `${k}: ${typeof v === "string" ? v : JSON.stringify(v)}`)
                  .join(" · ")}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
